/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React from 'react';
import styled from 'styled-components';
import { Checkbox, Icon, TableCell } from '../../atoms';
import { CheckboxTableHeadCell } from './dataListCell';
import { TableRowBasicStyle } from './dataListRow';
import { Column, DataListHeaderProps } from './types';

const HeaderContent = styled.div`
    display: flex;
    align-items: center;
`;

export const TextContent = styled.span`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    widht: 100%;
    display: block;
`;

function defaultRenderer<TItem, TData>(
    column: Column<TItem, TData>,
    index: number,
) {
    return column.onRenderHeader ? (
        column.onRenderHeader(column, index)
    ) : (
        <TextContent>{column.header}</TextContent>
    );
}

export function DataListHeader<TItem, TData>({
    items,
    columns,
    hideCheckBoxes,
    checked,
    onColumnClick,
    onChecked,
    onItemChecked,
    onColumnContextMenu,
    onRenderColumnHeader,
    tableId,
}: DataListHeaderProps<TItem, TData>): JSX.Element {
    function handleCheckAll(event: React.ChangeEvent<HTMLInputElement>) {
        onChecked(event.target.checked ? items : []);
        onItemChecked?.(event, null);
    }

    function doRenderColumnHeader(
        column: Column<TItem, TData>,
        index: number,
    ): React.ReactNode {
        return onRenderColumnHeader
            ? onRenderColumnHeader(column, index, defaultRenderer)
            : defaultRenderer(column, index);
    }

    return (
        <TableRowBasicStyle>
            {!hideCheckBoxes && (
                <CheckboxTableHeadCell header secondary>
                    <Checkbox
                        id={`data-list-head-all-checkbox-${tableId}`}
                        checked={
                            checked.length > 0 &&
                            items.length === checked.length
                        }
                        onChange={handleCheckAll}
                    />
                </CheckboxTableHeadCell>
            )}
            <>
                {columns.map((column, index) => (
                    <TableCell
                        style={column.style}
                        key={index}
                        header
                        secondary
                        onContextMenu={ev => {
                            if (onColumnContextMenu) {
                                onColumnContextMenu(column, ev);
                                ev.preventDefault();
                            }
                        }}
                        onClick={ev => {
                            if (onColumnClick) {
                                onColumnClick(ev, column);
                            }
                        }}
                        title={column.header}
                    >
                        <HeaderContent>
                            {column.isSorted && (
                                <Icon
                                    iconName={
                                        column.isSortedDescending
                                            ? 'arrow-down'
                                            : 'arrow-up'
                                    }
                                    title={
                                        column.isSortedDescending
                                            ? column.sortDescendingAriaLabel
                                            : column.sortAscendingAriaLabel
                                    }
                                />
                            )}
                            {doRenderColumnHeader(column, index)}
                        </HeaderContent>
                    </TableCell>
                ))}
            </>
        </TableRowBasicStyle>
    );
}
